import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import PortalLayout from '../../components/PortalLayout';

export default function PortalDashboard() {
  const [packages, setPackages] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showProfile, setShowProfile] = useState(false);
  const [phoneEdit, setPhoneEdit] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const token = localStorage.getItem('portal_token');

  useEffect(() => {
    if (!token) { navigate('/portal/login'); return; }
    const load = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [pkgRes, bkRes, meRes] = await Promise.all([
          fetch(`${backendUrl}/api/portal/packages`, { headers }),
          fetch(`${backendUrl}/api/portal/bookings`, { headers }),
          fetch(`${backendUrl}/api/portal/me`, { headers }),
        ]);
        if (pkgRes.status === 401 || bkRes.status === 401) { navigate('/portal/login'); return; }
        setPackages(await pkgRes.json());
        setBookings(await bkRes.json());
        const meData = await meRes.json();
        setMe(meData);
        setPhoneEdit(meData?.phone || '');
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const res = await fetch(`${backendUrl}/api/portal/me`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ phone: phoneEdit }),
      });
      if (!res.ok) throw new Error('Failed');
      setMe(await res.json());
      setShowProfile(false);
    } catch (e) { alert('Could not update profile'); }
    finally { setSavingProfile(false); }
  };

  const today = new Date(new Date().toDateString());
  const upcoming = bookings.filter((b) => new Date(`${b.booking_date}T00:00:00`) >= today && b.status !== 'cancelled');
  const past = bookings.filter((b) => new Date(`${b.booking_date}T00:00:00`) < today || b.status === 'cancelled');
  const completed = bookings.filter((b) => b.status === 'completed').length;

  const firstName = (me?.name || '').split(' ')[0] || (me?.name || '');

  return (
    <PortalLayout>
      {/* Hero */}
      <section
        className="relative overflow-hidden rounded-3xl p-6 sm:p-10 text-white mb-8 shadow-xl shadow-teal-500/10"
        style={{
          background:
            'radial-gradient(circle at 15% 20%, rgba(6,182,212,0.4) 0%, transparent 45%), radial-gradient(circle at 85% 90%, rgba(14,165,233,0.3) 0%, transparent 40%), linear-gradient(135deg, #0f766e 0%, #0d9488 45%, #0891b2 100%)',
        }}
      >
        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)', backgroundSize: '18px 18px' }}></div>
        <div className="relative flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-teal-100">Your Portal</div>
            <h1 className="text-3xl sm:text-4xl font-bold mt-2">
              {loading ? 'Loading…' : firstName ? `Welcome back, ${firstName}` : 'Welcome back'}
            </h1>
            <p className="mt-2 text-teal-50/90 max-w-lg text-[15px]">
              Book any of your bespoke packages below. Pricing is locked in — no surprises at checkout.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowProfile(true)}
              data-testid="edit-profile-btn"
              className="bg-white/15 hover:bg-white/25 backdrop-blur px-4 py-2.5 rounded-xl text-sm font-medium border border-white/20 transition"
            >
              <i className="ri-user-settings-line mr-1"></i> Profile
            </button>
            <Link
              to="/contact"
              className="bg-white text-teal-700 hover:bg-teal-50 px-4 py-2.5 rounded-xl text-sm font-semibold transition shadow-sm"
            >
              <i className="ri-customer-service-2-line mr-1"></i> Support
            </Link>
          </div>
        </div>

        {/* Stats strip */}
        <div className="relative mt-8 grid grid-cols-3 gap-3 sm:gap-4">
          <StatCard label="Packages" value={packages.length} icon="ri-price-tag-3-line" />
          <StatCard label="Upcoming" value={upcoming.length} icon="ri-calendar-event-line" />
          <StatCard label="Completed" value={completed} icon="ri-check-double-line" />
        </div>
      </section>

      {loading ? (
        <div className="text-center py-16 text-gray-500">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main column */}
          <div className="lg:col-span-2 space-y-8">
            {/* Packages */}
            <section>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Your Packages</h2>
                  <p className="text-sm text-gray-500">Assigned & ready to book.</p>
                </div>
                {packages.length > 0 && (
                  <span className="text-xs px-2.5 py-1 rounded-full bg-teal-100 text-teal-800 font-semibold">
                    {packages.length} available
                  </span>
                )}
              </div>

              {packages.length === 0 ? (
                <div className="bg-white border-2 border-dashed border-gray-200 rounded-2xl p-10 text-center">
                  <div className="h-14 w-14 mx-auto rounded-2xl bg-teal-50 flex items-center justify-center">
                    <i className="ri-inbox-line text-3xl text-teal-500"></i>
                  </div>
                  <p className="mt-4 text-gray-700 font-medium">No packages yet</p>
                  <p className="mt-1 text-sm text-gray-500 max-w-sm mx-auto">We'll email you the moment your first bespoke package is ready to book.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {packages.map((p) => <PackageCard key={p.assignment_id} p={p} />)}
                </div>
              )}
            </section>

            {/* Upcoming */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-4">Upcoming Bookings</h2>
              {upcoming.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-2xl p-6 text-sm text-gray-500 text-center">
                  <i className="ri-calendar-2-line text-gray-300 text-2xl"></i>
                  <p className="mt-2">Nothing on the calendar yet.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {upcoming.map((b) => <BookingRow key={b.id} b={b} />)}
                </div>
              )}
            </section>

            {/* Past */}
            {past.length > 0 && (
              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-4">Past Bookings</h2>
                <div className="space-y-3">
                  {past.slice(0, 10).map((b) => <BookingRow key={b.id} b={b} />)}
                </div>
              </section>
            )}
          </div>

          {/* Sidebar */}
          <aside className="space-y-6">
            {/* Profile summary */}
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full flex items-center justify-center text-white font-bold text-lg" style={{ background: 'linear-gradient(135deg, #0d9488 0%, #0891b2 100%)' }}>
                  {firstName.charAt(0).toUpperCase() || 'C'}
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900 truncate">{me?.name}</div>
                  <div className="text-xs text-gray-500 truncate">{me?.email}</div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-100 text-sm">
                <div className="flex justify-between text-gray-600"><span>Phone</span><span className="text-gray-900 font-medium">{me?.phone || '—'}</span></div>
                <button
                  onClick={() => setShowProfile(true)}
                  data-testid="sidebar-edit-profile-btn"
                  className="w-full mt-4 text-sm font-medium text-teal-700 hover:text-teal-800 bg-teal-50 hover:bg-teal-100 py-2 rounded-lg transition"
                >
                  <i className="ri-edit-line"></i> Edit profile
                </button>
              </div>
            </div>

            {/* Help card */}
            <div className="rounded-2xl p-5 text-white relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0f766e 0%, #0891b2 100%)' }}>
              <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-white/10"></div>
              <div className="relative">
                <div className="text-[11px] font-semibold uppercase tracking-widest text-teal-100">Need a hand?</div>
                <h3 className="text-lg font-bold mt-1">We're one message away</h3>
                <p className="text-teal-50/90 text-sm mt-1">Custom shoot? Multi-property brief? Chat with us.</p>
                <div className="mt-4 flex flex-col gap-2">
                  <a href="tel:+61431323137" className="bg-white/15 hover:bg-white/25 backdrop-blur px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
                    <i className="ri-phone-line"></i> +61 431 323 137
                  </a>
                  <a href="mailto:media@matthewminchio.com" className="bg-white/15 hover:bg-white/25 backdrop-blur px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
                    <i className="ri-mail-line"></i> Email us
                  </a>
                  <Link to="/contact" className="bg-white text-teal-700 hover:bg-teal-50 px-3 py-2 rounded-lg text-sm font-semibold flex items-center gap-2">
                    <i className="ri-message-3-line"></i> Send a message
                  </Link>
                </div>
              </div>
            </div>

            {/* Policy note */}
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
              <div className="flex items-start gap-3">
                <i className="ri-shield-check-line text-2xl text-amber-600"></i>
                <div>
                  <div className="font-semibold text-amber-900 text-sm">Cancellation policy</div>
                  <p className="text-xs text-amber-800/90 mt-1">Cancellations or reschedules within 24 hours of a booking incur a $220 (inc GST) late fee. Weather cancellations waived at admin discretion.</p>
                </div>
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* Profile Modal */}
      {showProfile && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowProfile(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">Your profile</h3>
              <button onClick={() => setShowProfile(false)} className="text-gray-400 text-2xl leading-none">×</button>
            </div>
            <form onSubmit={saveProfile} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input type="text" value={me?.name || ''} disabled className="w-full px-4 py-2.5 border border-gray-200 bg-gray-50 rounded-xl text-gray-500" />
                <p className="text-xs text-gray-500 mt-1">Contact us if you need to update your name.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" value={me?.email || ''} disabled className="w-full px-4 py-2.5 border border-gray-200 bg-gray-50 rounded-xl text-gray-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={phoneEdit}
                  onChange={(e) => setPhoneEdit(e.target.value)}
                  placeholder="e.g. 0412 345 678"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none"
                  data-testid="profile-phone-input"
                />
                <p className="text-xs text-gray-500 mt-1">So we can call if we're heading your way.</p>
              </div>
              <div className="pt-2 flex gap-2">
                <button type="submit" disabled={savingProfile} data-testid="profile-save-btn" className="flex-1 text-white py-2.5 rounded-xl font-medium disabled:opacity-50" style={{ background: 'linear-gradient(135deg, #0d9488 0%, #0891b2 100%)' }}>
                  {savingProfile ? 'Saving…' : 'Save changes'}
                </button>
                <button type="button" onClick={() => setShowProfile(false)} className="px-4 py-2.5 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </PortalLayout>
  );
}

function StatCard({ label, value, icon }) {
  return (
    <div className="bg-white/15 backdrop-blur border border-white/20 rounded-2xl px-3 sm:px-5 py-3 sm:py-4">
      <div className="flex items-center gap-2 sm:gap-3">
        <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-xl bg-white/15 flex items-center justify-center">
          <i className={`${icon} text-lg text-white`}></i>
        </div>
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-teal-100/90">{label}</div>
          <div className="text-lg sm:text-2xl font-bold text-white leading-tight">{value}</div>
        </div>
      </div>
    </div>
  );
}

function PackageCard({ p }) {
  return (
    <div className="group bg-white rounded-2xl border border-gray-200 p-6 hover:border-teal-300 hover:shadow-xl hover:shadow-teal-500/5 transition relative overflow-hidden">
      <div className="absolute top-0 right-0 h-32 w-32 -translate-y-16 translate-x-16 rounded-full opacity-0 group-hover:opacity-20 transition-opacity" style={{ background: 'radial-gradient(circle, #0d9488, transparent 70%)' }}></div>
      <div className="relative">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-lg font-bold text-gray-900 leading-tight">{p.name}</h3>
          <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-teal-100 text-teal-800">Yours</span>
        </div>
        <div className="mt-3 flex items-baseline gap-1.5">
          <span className="text-3xl font-bold" style={{ backgroundImage: 'linear-gradient(135deg, #0d9488, #0891b2)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>A${Number(p.price).toFixed(0)}</span>
          <span className="text-xs text-gray-500 font-medium">incl. GST</span>
        </div>
        <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
          <i className="ri-time-line"></i> {p.duration_minutes || 120} min on-site
        </div>
        {p.description && <p className="mt-3 text-sm text-gray-600 line-clamp-2">{p.description}</p>}
        {(p.features || []).length > 0 && (
          <ul className="mt-3 space-y-1.5">
            {p.features.map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                <i className="ri-check-line text-teal-600 mt-0.5"></i>
                <span>{f}</span>
              </li>
            ))}
          </ul>
        )}
        {/photo/i.test([p.name, p.description, ...(p.features || [])].join(' ')) && (
          <p data-testid={`cubicasa-note-${p.assignment_id}`} className="mt-4 text-sm text-green-600 font-bold italic leading-snug">
            "CubiCasa Floor Plans Included with All Interior Photo Shoots."
          </p>
        )}
        <Link
          to={`/portal/book/${p.assignment_id}`}
          data-testid={`book-${p.assignment_id}`}
          className="mt-5 w-full inline-flex items-center justify-center gap-1.5 text-white py-2.5 rounded-xl font-semibold text-sm transition shadow-md shadow-teal-500/20 hover:shadow-teal-500/40"
          style={{ background: 'linear-gradient(135deg, #0d9488 0%, #0891b2 100%)' }}
        >
          Book this package <i className="ri-arrow-right-line"></i>
        </Link>
      </div>
    </div>
  );
}

function BookingRow({ b }) {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const token = localStorage.getItem('portal_token');
  const [downloading, setDownloading] = React.useState(false);

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    confirmed: 'bg-green-100 text-green-800 border-green-200',
    completed: 'bg-blue-100 text-blue-800 border-blue-200',
    cancelled: 'bg-red-100 text-red-800 border-red-200',
    cancellation_requested: 'bg-orange-100 text-orange-800 border-orange-200',
  };
  const total = b.package?.total_price || 0;
  const invoiceNum = b.xero_invoice_number;
  const hasInvoice = !!b.xero_invoice_id;
  const canCancel = b.cancellation_token && b.status !== 'cancelled' && b.status !== 'completed';

  const downloadInvoice = async () => {
    if (!hasInvoice || downloading) return;
    setDownloading(true);
    try {
      const res = await fetch(`${backendUrl}/api/portal/bookings/${b.id}/invoice`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        let msg = 'Could not download invoice.';
        try { const j = await res.json(); if (j.detail) msg = j.detail; } catch (_) { /* pdf may not be JSON */ }
        alert(msg);
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      // Open in new tab for viewing/printing; browser offers Save-As from there.
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener';
      link.download = `invoice-${invoiceNum || b.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 4000);
    } catch (e) {
      alert('Could not download invoice.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl px-5 py-4 flex items-center justify-between gap-4 hover:border-teal-200 transition">
      <div className="min-w-0 flex items-center gap-4">
        <div className="h-12 w-12 rounded-xl bg-teal-50 flex flex-col items-center justify-center text-teal-700 shrink-0">
          <div className="text-[10px] font-bold uppercase leading-none">{new Date(`${b.booking_date}T00:00:00`).toLocaleString('en-AU', { month: 'short' })}</div>
          <div className="text-lg font-bold leading-tight">{new Date(`${b.booking_date}T00:00:00`).getDate()}</div>
        </div>
        <div className="min-w-0">
          <div className="font-semibold text-gray-900 truncate">{b.package?.package_name || 'Session'}</div>
          <div className="text-xs text-gray-500 mt-0.5">{b.booking_time}</div>
          {b.property_address && (
            <div className="text-xs text-gray-400 mt-0.5 truncate">
              <i className="ri-map-pin-line"></i> {b.property_address}
            </div>
          )}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-bold text-gray-900">A${Number(total).toFixed(2)}</div>
        <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium border ${statusColors[b.status] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>{(b.status || '').replace('_', ' ')}</span>
        <div className="mt-2 flex flex-col items-end gap-1.5">
          {hasInvoice ? (
            <button
              type="button"
              onClick={downloadInvoice}
              disabled={downloading}
              data-testid={`download-invoice-${b.id}`}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-teal-700 hover:text-teal-800 bg-teal-50 hover:bg-teal-100 px-2 py-0.5 rounded-md disabled:opacity-50 transition"
              title={invoiceNum ? `Download invoice ${invoiceNum}` : 'Download invoice'}
            >
              <i className={downloading ? 'ri-loader-4-line animate-spin' : 'ri-download-2-line'}></i>
              {downloading ? 'Preparing…' : `Invoice${invoiceNum ? ` #${invoiceNum}` : ''}`}
            </button>
          ) : invoiceNum ? (
            <span className="text-[11px] text-gray-500">Invoice #{invoiceNum}</span>
          ) : null}
          {canCancel && (
            <a
              href={`/cancel/${b.cancellation_token}`}
              data-testid={`reschedule-booking-${b.id}`}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 px-3 py-1.5 rounded-lg shadow-sm shadow-teal-500/20 transition"
            >
              <i className="ri-calendar-event-line"></i> Reschedule / Cancel
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
